import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { useTranslate } from "ra-core";
import { ExternalLink, ImageIcon, UploadCloud } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { WhatsAppTemplate, WhatsAppTemplateMediaAsset } from "../types";
import {
  dropzoneAccept,
  formatBytes,
  type MediaHeaderFormat,
  mediaSizeLimitBytes,
} from "./templateMedia";

export type BroadcastHeaderMediaValue = {
  mode: "template" | "override";
  asset: WhatsAppTemplateMediaAsset | null;
};

export function defaultHeaderMediaValue(
  template?: WhatsAppTemplate | null,
): BroadcastHeaderMediaValue {
  // Imported templates without a stored asset cannot reuse a template image at
  // send time, so the operator must upload one.
  return {
    mode: template?.header_media_asset_id ? "template" : "override",
    asset: null,
  };
}

const NOUN: Record<MediaHeaderFormat, string> = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
};

export function BroadcastHeaderMediaField({
  template,
  format,
  value,
  onChange,
  mediaUploadEnabled,
  onUpload,
}: {
  template: WhatsAppTemplate;
  format: MediaHeaderFormat;
  value: BroadcastHeaderMediaValue;
  onChange: (value: BroadcastHeaderMediaValue) => void;
  mediaUploadEnabled: boolean;
  onUpload: (file: File) => Promise<WhatsAppTemplateMediaAsset>;
}) {
  const translate = useTranslate();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noun = NOUN[format] ?? "image";
  const templateHasSendableAsset = !!template.header_media_asset_id;
  const limitBytes = mediaSizeLimitBytes(format);

  const previewUrl =
    value.mode === "override"
      ? value.asset?.preview_url
      : template.header_media_url;
  const previewLabel =
    value.mode === "override"
      ? value.asset?.file_name
      : template.header_media_file_name;

  const onDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length) {
        setError(
          translate("crm.whatsapp.broadcasts.header_media_format_mismatch", {
            _: "That file type isn't allowed for this template.",
          }),
        );
        return;
      }
      const file = accepted[0];
      if (!file) return;
      if (file.size > limitBytes) {
        setError(
          translate("crm.whatsapp.broadcasts.header_media_too_large", {
            _: "That file is too large (max %{limit}).",
            limit: formatBytes(limitBytes),
          }),
        );
        return;
      }
      setError(null);
      setUploading(true);
      try {
        const asset = await onUpload(file);
        onChange({ mode: "override", asset });
      } catch (uploadError) {
        setError(
          uploadError instanceof Error
            ? uploadError.message
            : translate("crm.whatsapp.error", { _: "Something went wrong." }),
        );
      } finally {
        setUploading(false);
      }
    },
    [limitBytes, onChange, onUpload, translate],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: dropzoneAccept(format),
    maxFiles: 1,
    multiple: false,
    disabled: !mediaUploadEnabled || uploading,
    onDrop,
  });

  const showDropzone = value.mode === "override" || !templateHasSendableAsset;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>
          {translate("crm.whatsapp.broadcasts.header_media", {
            _: "Header image",
          })}
        </Label>
        {templateHasSendableAsset && (
          <div className="flex gap-1.5 text-xs">
            <ModeButton
              active={value.mode === "template"}
              onClick={() => {
                setError(null);
                onChange({ mode: "template", asset: null });
              }}
            >
              {translate("crm.whatsapp.broadcasts.header_media_use_template", {
                _: "Use template image",
              })}
            </ModeButton>
            <ModeButton
              active={value.mode === "override"}
              disabled={!mediaUploadEnabled}
              onClick={() => {
                setError(null);
                onChange({ mode: "override", asset: value.asset });
              }}
            >
              {translate(
                "crm.whatsapp.broadcasts.header_media_upload_different",
                { _: "Upload a different image" },
              )}
            </ModeButton>
          </div>
        )}
      </div>

      <MediaThumb format={format} url={previewUrl} label={previewLabel} />

      {value.mode === "template" && templateHasSendableAsset && (
        <p className="text-xs text-muted-foreground">
          {translate("crm.whatsapp.broadcasts.header_media_from_template", {
            _: "Using the template's image. People will receive this.",
          })}
        </p>
      )}

      {!templateHasSendableAsset && (
        <p className="text-xs text-muted-foreground">
          {translate(
            "crm.whatsapp.broadcasts.header_media_template_unsendable",
            {
              _: "This template was imported without a stored image. Upload one to use it in a broadcast.",
            },
          )}
        </p>
      )}

      {showDropzone &&
        (mediaUploadEnabled ? (
          <div
            {...getRootProps()}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground transition-colors",
              isDragActive && "border-primary bg-primary/5 text-primary",
              uploading && "cursor-not-allowed opacity-60",
            )}
          >
            <input {...getInputProps()} />
            <UploadCloud className="size-5" />
            <span>
              {uploading
                ? translate("crm.whatsapp.broadcasts.header_media_uploading", {
                    _: "Uploading…",
                  })
                : translate("crm.whatsapp.broadcasts.header_media_dropzone", {
                    _: "Drag an image here, or click to choose",
                  })}
            </span>
            <span className="text-xs">
              {translate("crm.whatsapp.broadcasts.header_media_dropzone_hint", {
                _: "%{noun} · up to %{limit}",
                noun: noun.toUpperCase(),
                limit: formatBytes(limitBytes),
              })}
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {translate("crm.whatsapp.capabilities.media_disabled", {
              _: "Media upload is not available in this environment.",
            })}
          </p>
        ))}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function ModeButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-md border px-2 py-1 transition-colors",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      {children}
    </button>
  );
}

function MediaThumb({
  format,
  url,
  label,
}: {
  format: MediaHeaderFormat;
  url?: string | null;
  label?: string | null;
}) {
  const display = label || format;

  if (url && format === "IMAGE") {
    return (
      <img
        alt={display}
        className="aspect-video w-full rounded-md border object-cover"
        src={url}
      />
    );
  }
  if (url && format === "VIDEO") {
    return (
      <video
        className="aspect-video w-full rounded-md border object-cover"
        controls
        src={url}
      />
    );
  }
  if (url && format === "DOCUMENT") {
    return (
      <a
        className="flex min-h-16 items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm"
        href={url}
        rel="noreferrer"
        target="_blank"
      >
        <span className="min-w-0 truncate">{display}</span>
        <ExternalLink className="size-4 shrink-0" />
      </a>
    );
  }
  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed bg-background text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <ImageIcon className="size-4" />
        <span>{display}</span>
      </div>
    </div>
  );
}
